import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../constants";
import { PICKS_A_DAY, PICKUP_OFFSETS } from "../pickupSlots";
import { formatBlackoutPeriod } from "../blackouts/blackoutHelpers";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 },
  question: { fontSize: 13, fontWeight: 600, color: T.fg },
  chips: { display: "flex", gap: 6, flexWrap: "wrap" },
  chip: {
    border: `1px solid ${T.border}`,
    background: T.elevated,
    borderRadius: 999,
    padding: "7px 12px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
    color: T.fg,
    '&[aria-pressed="true"]': {
      background: T.accent,
      color: T.accentFg,
      borderColor: T.primary,
      fontWeight: 600,
    },
    "&:disabled": {
      color: T.subtleFg,
      background: T.surface,
      cursor: "not-allowed",
      textDecoration: "line-through",
    },
  },
  times: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: 6,
  },
  time: {
    border: `1px solid ${T.border}`,
    background: T.elevated,
    borderRadius: 6,
    padding: "6px 4px",
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
    color: T.fg,
    fontVariantNumeric: "tabular-nums",
    '&[aria-pressed="true"]': {
      background: T.accent,
      color: T.accentFg,
      borderColor: T.primary,
      fontWeight: 600,
    },
    "&:disabled": {
      color: T.subtleFg,
      background: T.surface,
      cursor: "not-allowed",
      textDecoration: "line-through",
    },
  },
  muted: { fontSize: 12, color: T.mutedFg },
  blackoutList: { margin: "4px 0 0", paddingLeft: 18 },
});

/*
 * When the car is wanted: now, a few minutes out, or an exact time off the
 * grid. Times nobody is rostered for stay on screen struck through rather than
 * disappearing, so the desk can see the shift rather than guess at it.
 */
export default function PickupTimePicker({
  offset,
  onOffset,
  slots,
  bookableDays,
  unavailableOffsets = [],
  chosenTime,
  onChooseTime,
  collectionHours = "",
  blackoutPeriods = [],
  timezone,
  disabled = false,
}) {
  const s = useStyles();

  /* A day with nothing bookable left on it opens a grid of struck-through
     times, so the chip that would open it is not offered at all. */
  const offsets = PICKUP_OFFSETS.filter(
    (item) =>
      (item.key !== "LATER_TODAY" || bookableDays.TODAY) &&
      (item.key !== "TOMORROW" || bookableDays.TOMORROW),
  );
  const picksADay = PICKS_A_DAY.includes(offset);
  const struckOut = slots.some((slot) => !slot.available);

  return (
    <div className={s.root}>
      <div className={s.question}>When is the car wanted?</div>

      <div className={s.chips} role="group" aria-label="Pickup time">
        {offsets.map((item) => {
          const closed = unavailableOffsets.includes(item.key);

          return (
            <button
              key={item.key}
              type="button"
              className={s.chip}
              aria-pressed={item.key === offset}
              aria-label={closed ? `${item.label} · not available` : undefined}
              title={closed ? "Pickup is unavailable at this time" : undefined}
              disabled={disabled || closed}
              onClick={() => onOffset(item.key)}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {picksADay && (
        <>
          <div
            className={s.times}
            role="group"
            aria-label="Available pickup times"
          >
            {slots.map((slot) => (
              <button
                key={slot.iso}
                type="button"
                className={s.time}
                aria-pressed={slot.iso === chosenTime}
                aria-label={
                  slot.available ? undefined : `${slot.label} · not available`
                }
                title={
                  slot.available
                    ? undefined
                    : slot.blackout
                      ? "Blackout period"
                      : "Outside collection hours"
                }
                disabled={disabled || !slot.available}
                onClick={() => onChooseTime(slot.iso)}
              >
                {slot.label}
              </button>
            ))}
          </div>
          {(collectionHours || blackoutPeriods.length > 0) && (
            <div className={s.muted}>
              {collectionHours && (
                <div>
                  Collections run {collectionHours}.
                  {struckOut ? " Times outside them cannot be booked." : ""}
                </div>
              )}
              {blackoutPeriods.length > 0 && (
                <div>
                  <div>Active blackout periods:</div>
                  <ul className={s.blackoutList}>
                    {blackoutPeriods.map((period) => (
                      <li key={period.id}>
                        {period.name ? `${period.name}: ` : ""}
                        {formatBlackoutPeriod(period, timezone)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
